import classNames from 'classnames'
import { getLocalItem, setLocalItem } from 'components/utils'
import { ReactNode, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { DisplayMode } from 'types/customParams'
import { CustomColorsRef, IconSizes } from 'types/general'

import { i18n } from '../../../i18n'
import IconRender from '../IconRender'
import Loading from '../loading/Loading'

interface ModalProps {
  isOpen: boolean
  isLoading?: boolean
  onClose?: () => void | null
  icon?: string
  title?: string
  subtitle?: string
  headerVariant?: 'default' | 'light'
  children?: ReactNode
  className?: string
  customHeaderClasses?: string
  customBodyClasses?: string
  allowChangeDisplayMode?: boolean
  manualSize?: string
}

const Modal = ({
  isOpen,
  isLoading,
  onClose,
  icon,
  title,
  subtitle,
  headerVariant = 'default',
  children,
  className,
  customHeaderClasses,
  customBodyClasses,
  allowChangeDisplayMode = true,
  manualSize,
}: ModalProps) => {
  const isLightHeader = headerVariant === 'light'
  const [modalSize, setModalSize] = useState<string | undefined | null>(
    getLocalItem('displayMode')
  )

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('overflow-hidden')
    } else {
      document.body.classList.remove('overflow-hidden')
    }

    return () => {
      document.body.classList.remove('overflow-hidden')
    }
  }, [isOpen])

  useEffect(() => {
    const localValue = getLocalItem('displayMode')
    if (!localValue) {
      setLocalItem('displayMode', DisplayMode.Modal)
      setModalSize(DisplayMode.Modal)
    } else {
      setModalSize(localValue)
    }
  }, [])

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'displayMode') {
        setModalSize(event.newValue)
      }
    }

    window.addEventListener('storage', handleStorageChange)

    const interval = setInterval(() => {
      const current = getLocalItem('displayMode')
      setModalSize(prev => (prev !== current ? current : prev))
    }, 500)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      clearInterval(interval)
    }
  }, [])

  const getModalSize = () => {
    if (manualSize) {
      return manualSize
    }

    if (allowChangeDisplayMode) {
      return modalSize
    }

    return DisplayMode.Modal
  }

  if (!isOpen) return null

  return createPortal(
    <div
      className={classNames(
        'fixed inset-0 z-[60] flex justify-center bg-black bg-opacity-50 overflow-y-auto pt-16',
        {
          'items-center': DisplayMode.Modal === getModalSize(),
        }
      )}
      onClick={(e) => {
        // Prevent closing modal when clicking overlay
        e.stopPropagation()
      }}
    >
      <div
        className={classNames(
          'bg-white relative flex flex-col shadow-md shadow-neutral-600 transition-all duration-500 ease-in-out',
          {
            'w-full sm:w-full min-h-[90vh] h-fit rounded-none rounded-b-2xl':
              DisplayMode.FullPage === getModalSize(),
            'max-h-[70vh] h-[70vh] w-[70%] rounded-lg rounded-b-2xl':
              DisplayMode.Modal === getModalSize(),
          },
          className
        )}
      >
        <div
          className={classNames(
            'rounded-t-lg cursor-default p-4 min-h-[72px] relative',
            isLightHeader
              ? 'bg-white border-b border-gray-200 flex flex-col justify-center items-center px-12 py-5'
              : 'bg-imovSecondary text-white flex flex-row items-center h-16',
            customHeaderClasses
          )}
        >
          {!isLightHeader && icon && (
            <IconRender
              icon={icon}
              size={IconSizes.XL}
              color={
                DisplayMode.Modal === getModalSize()
                  ? CustomColorsRef.White
                  : CustomColorsRef.Primary
              }
            />
          )}
          {title && (
            <h2
              className={classNames(
                'font-bold uppercase tracking-wide',
                isLightHeader
                  ? 'text-imovSecondary text-lg sm:text-xl text-center leading-snug'
                  : 'text-white md:text-2xl font-semibold ml-4 mr-8'
              )}
            >
              {title}
            </h2>
          )}
          {isLightHeader && subtitle && (
            <p className="mt-1.5 max-w-sm text-center text-sm leading-relaxed text-gray-500">
              {subtitle}
            </p>
          )}
          {onClose && (
            <button
              type="button"
              className={classNames(
                'absolute right-3 p-1.5 hover:opacity-80',
                isLightHeader ? 'top-3 text-imovSecondary' : 'text-white top-1/2 -translate-y-1/2'
              )}
              onClick={onClose}
              aria-label={i18n.translate('common.close')}
            >
              <IconRender
                icon="regular-circle-xmark"
                color={isLightHeader ? CustomColorsRef.Primary : CustomColorsRef.White}
                hoverColor={CustomColorsRef.WhiteShadow}
                size={IconSizes.XL}
              />
            </button>
          )}
          {!isLightHeader && (
            <div className="absolute right-3 gap-2 flex items-center">
              {onClose && (
                <button className="pl-1 hover:opacity-80" onClick={onClose}>
                  <IconRender
                    icon={
                      getModalSize() === DisplayMode.Modal
                        ? 'regular-circle-xmark'
                        : 'regular-left'
                    }
                    color={
                      getModalSize() === DisplayMode.Modal
                        ? CustomColorsRef.White
                        : CustomColorsRef.Primary
                    }
                    hoverColor={CustomColorsRef.WhiteShadow}
                    size={IconSizes.XL}
                  />
                </button>
              )}
            </div>
          )}
        </div>
        <div
          className={classNames(
            'flex-grow w-full overflow-y-auto text-imovSecondary bg-gray-50 rounded-b-2xl',
            customBodyClasses
          )}
        >
          <div
            className="h-full w-full overflow-y-auto"
            style={{ scrollbarWidth: 'thin' }}
          >
            {isLoading ? (
              <div className="h-full w-full flex align-middle justify-center">
                <Loading />
              </div>
            ) : (
              children
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default Modal
